<script setup>
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
} from "vue";
import { useStore } from "vuex";
import { SpeedInsights } from "@vercel/speed-insights/vue";
import { collection, getDocs, getDoc, doc, onSnapshot, query, orderBy, where } from "firebase/firestore";
import { auth } from "@/firebase";
import { db } from "@/firebase";
import AppTopbar from "@/layout/AppTopbar.vue";
import AppSidebar from "@/layout/AppSidebar.vue";
import { useLayout } from "@/layout/composables/layout";
import { Vue3Marquee } from "vue3-marquee";
import { formatPrice } from "@/services/formatPrice";

// Import NotificationComponent
import NotificationComponent from "@/views/auction/components/Notification.vue";
import TermsAndConditions from "@/views/auction/components/TermsAndConditions.vue";

// Donations ticker
const donations = ref([]);
const listenedDonationItems = new Set();

const setupDonationPurchaseListener = (itemId) => {
  if (listenedDonationItems.has(itemId)) return;
  listenedDonationItems.add(itemId);

  const purchasesRef = collection(db, `auctionItems/${itemId}/purchase`);
  const purchasesQuery = query(purchasesRef, orderBy("timestamp", "desc"));

  onSnapshot(purchasesQuery, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type !== "added") return;
      const purchase = change.doc.data();
      const formatted = formatPrice(purchase.userBid, purchase.currency || "SEK");
      const msg = `Donation of ${formatted} by ${purchase.user?.name || "Anonymous"}`;
      if (!donations.value.some(d => d.id === change.doc.id)) {
        donations.value.push({ id: change.doc.id, message: msg });
        if (donations.value.length > 100) donations.value.shift();
      }
    });
  });
};

const fetchDonationsTicker = () => {
  const q = query(collection(db, "auctionItems"), where("isDonationItem", "==", true));
  onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === "added") setupDonationPurchaseListener(change.doc.id);
    });
  });
};

const categories = ref([]);

const showTermsPopup = ref(false);
const { layoutConfig, layoutState, isSidebarActive } = useLayout();
const outsideClickListener = ref(null);
const body = document.getElementsByTagName("body")[0];
// Vuex store
const store = useStore();
const userDocId = computed(() => store.getters.userDocId);

// Check user's agreement status
const checkUserAgreement = async () => {
  if (userDocId.value) {
    const userDoc = await getDoc(doc(db, "users", userDocId.value));

    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.agreedToTerms) {
        showTermsPopup.value = false;
      } else {
        showTermsPopup.value = true;
      }
    } else {
      showTermsPopup.value = true;
    }
  }
};
const fetchCategories = async () => {
  const querySnapshot = await getDocs(collection(db, "category"));
  categories.value = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

watch(isSidebarActive, (newVal) => {
  if (newVal) {
    bindOutsideClickListener();
  } else {
    unbindOutsideClickListener();
  }
});

const containerClass = computed(() => ({
  "layout-theme-light": layoutConfig.darkTheme.value === "light",
  "layout-theme-dark": layoutConfig.darkTheme.value === "dark",
  "layout-overlay": layoutConfig.menuMode.value === "overlay",
  "layout-static": layoutConfig.menuMode.value === "static",
  "layout-static-inactive":
    layoutState.staticMenuDesktopInactive.value &&
    layoutConfig.menuMode.value === "static",
  "layout-overlay-active": layoutState.overlayMenuActive.value,
  "layout-mobile-active": layoutState.staticMenuMobileActive.value,
  "p-ripple-disabled": layoutConfig.ripple.value === false,
}));

const bindOutsideClickListener = () => {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        layoutState.overlayMenuActive.value = false;
        layoutState.staticMenuMobileActive.value = false;
        layoutState.menuHoverActive.value = false;
      }
    };
    document.addEventListener("click", outsideClickListener.value);
  }
};

const unbindOutsideClickListener = () => {
  if (outsideClickListener.value) {
    document.removeEventListener("click", outsideClickListener.value);
    outsideClickListener.value = null;
  }
};

const isOutsideClicked = (event) => {
  const sidebarEl = document.querySelector(".layout-sidebar");
  const topbarEl = document.querySelector(".layout-menu-button");
  return !(
    sidebarEl.isSameNode(event.target) ||
    sidebarEl.contains(event.target) ||
    topbarEl.isSameNode(event.target) ||
    topbarEl.contains(event.target)
  );
};

onMounted(async () => {
  await fetchCategories();
  fetchDonationsTicker();
});

onBeforeMount(() => {
  store.commit("setShowSidenav", true);
  store.state.showNavbar = true;
  store.state.hideConfigButton = true;
  store.commit("toggleDefaultLayout");
  body.classList.remove("bg-gray-100");
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  store.commit("setShowSidenav", false);
  store.commit("toggleDefaultLayout");
  body.classList.add("bg-gray-100");
});

auth.onAuthStateChanged((user) => {
  store.dispatch("fetchUser", user);
  checkUserAgreement();
});
</script>

<template>
  <div class="layout-wrapper layout-static" :class="containerClass">
    <app-topbar></app-topbar>
    <div class="layout-sidebar">
      <app-sidebar></app-sidebar>
    </div>
    <div class="layout-main-container" :class="{ 'has-donation-ticker': donations.length > 0 }">
      <div class="layout-main">
        <router-view />
        <!-- Add NotificationComponent here -->
        <NotificationComponent />
        <SpeedInsights />
        <!-- Display the Terms and Conditions popup if required -->
        <terms-and-conditions v-if="showTermsPopup" :user-id="userDocId" />
      </div>
    </div>
    <!-- Donations ticker at the bottom of all auction pages -->
    <div v-if="donations.length > 0" class="auction-donation-ticker">
      <Vue3Marquee :duration="40 + donations.length * 5">
        <span
          v-for="donation in donations"
          :key="donation.id"
          class="auction-ticker-item"
          v-html="donation.message"
        ></span>
      </Vue3Marquee>
    </div>
  </div>
</template>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Playfair+Display&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap");
</style>

<style scoped>
.auction-donation-ticker {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 44px;
  background: linear-gradient(90deg, #0a1336 0%, #0f1a3d 50%, #0a1336 100%);
  border-top: 1px solid rgba(195, 153, 53, 0.45);
  box-shadow:
    0 -2px 18px rgba(195, 153, 53, 0.15),
    inset 0 1px 0 rgba(195, 153, 53, 0.2);
  display: flex;
  align-items: center;
  overflow: hidden;
}

.auction-ticker-item {
  font-family: "Playfair Display", serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin-right: 60px;
  white-space: nowrap;
  background: linear-gradient(
    135deg,
    #fff4d1 0%,
    #e4c066 40%,
    #c39935 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 10px rgba(195, 153, 53, 0.3));
}

.auction-ticker-item :deep(small) {
  -webkit-text-fill-color: #c39935;
  color: #c39935;
  background: none;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-left: 0.2em;
  font-size: 0.7em;
}

.has-donation-ticker {
  padding-bottom: 44px;
}
</style>
